import { InputBase, InputBaseProps, Paper, Popper } from "@mui/material";
import {
  GridRenderEditCellParams,
  useGridApiContext,
} from "@mui/x-data-grid-premium";
import { useCallback, useEffect, useRef, useState } from "react";

export default function ExpandInput(
  props: GridRenderEditCellParams<any, string>
) {
  const { id, field, value, colDef, hasFocus, handleUpdateRowData, rowData } =
    props;
  const ref = useRef<HTMLTextAreaElement>();
  const [valueState, setValueState] = useState(value);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>();
  const apiRef = useGridApiContext();
  const lastCursorPositionRef = useRef<number | null>(null);

  // Handle focus and value updates
  useEffect(() => {
    setValueState(value);
  }, [hasFocus, value]);

  // Update cell value in the grid
  const updateCellValue = useCallback(
    (newValue: string) => {
      if (ref.current) {
        const currentCursorPosition = ref.current.selectionStart;

        lastCursorPositionRef.current = currentCursorPosition;
      }

      setValueState(newValue);
      apiRef.current.setEditCellValue(
        { id, field, value: newValue, debounceMs: 200 },
        undefined
      );
    },
    [apiRef, id, field]
  );

  const handleChange = useCallback<NonNullable<InputBaseProps["onChange"]>>(
    (event) => {
      updateCellValue(event.target.value);
    },
    [updateCellValue]
  );

  const handleKeyDown = useCallback<NonNullable<InputBaseProps["onKeyDown"]>>(
    (event) => {
      if (event.key === "Enter") {
        event.preventDefault();
        event.stopPropagation();

        // Insert a newline in the text
        const textarea = event.target as HTMLTextAreaElement;
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const value = textarea.value;
        const newValue =
          value.substring(0, start) + "\n" + value.substring(end);

        updateCellValue(newValue);

        // Move cursor after the newline
        setTimeout(() => {
          textarea.selectionStart = textarea.selectionEnd = start + 1;
        }, 0);

        // Update the row data
        const row = rowData.find((row: { id: string }) => row.id === id);
        const rowIndex = rowData.findIndex(
          (row: { id: string }) => row.id === id
        );
        if (row) {
          const updatedRowData = [
            ...rowData.slice(0, rowIndex),
            {
              ...row,
              [field]: newValue,
            },
            ...rowData.slice(rowIndex + 1),
          ];
          handleUpdateRowData(updatedRowData);
        }
      } else if (event.key === "Tab" || event.key === "Escape") {
        const params = apiRef.current.getCellParams(id, field);
        apiRef.current.publishEvent("cellKeyDown", params, event);
      }
    },
    [apiRef, id, field, updateCellValue, handleUpdateRowData, rowData]
  );

  return (
    <div style={{ position: "relative", alignSelf: "flex-start" }}>
      <div
        ref={(el) => {
          if (anchorEl !== el) {
            setAnchorEl(el);
          }
        }}
        style={{
          height: 1,
          width: Math.floor(colDef.computedWidth - 2),
          display: "block",
          position: "absolute",
          top: "-10px",
        }}
      />
      {anchorEl && (
        <Popper open anchorEl={anchorEl} placement="bottom-start">
          <Paper
            elevation={1}
            sx={{
              borderRadius: 0,
              p: 1,
              minWidth: colDef.computedWidth - 2,
              minHeight: 46,
              transform: "translateY(0px)",
              "&:focus-within": {
                border: "solid #074EE8 1px",
              },
            }}
          >
            <InputBase
              inputRef={ref}
              multiline
              value={valueState}
              sx={{ textarea: { resize: "both" }, width: "100%" }}
              onChange={handleChange}
              autoFocus
              onFocus={(e) => {
                const cursorPos =
                  lastCursorPositionRef.current ?? e.currentTarget.value.length;

                e.currentTarget.setSelectionRange(cursorPos, cursorPos);
              }}
              onKeyDown={handleKeyDown}
            />
          </Paper>
        </Popper>
      )}
    </div>
  );
}
